import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Badge, Button, Card, Input, Table, Tbody, Td, Th, Thead, Tr } from '@diwata/ironvale'

export function CircleDetail() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()
  const qc = useQueryClient()

  const { data: circle } = useQuery({ queryKey: ['circle', id], queryFn: () => api.getCircle(id!), enabled: !!id })
  const { data: members = [] } = useQuery({ queryKey: ['members', id], queryFn: () => api.listMembers(id!), enabled: !!id })
  const { data: realms = [] } = useQuery({ queryKey: ['realms', id], queryFn: () => api.listRealms(id!), enabled: !!id })

  // The Circle's Guild familiars — one roster query per realm, flattened.
  const familiarQueries = useQueries({
    queries: realms.map((r) => ({
      queryKey: ['realm-familiars', r.id],
      queryFn: () => api.listRealmFamiliars(r.id),
    })),
  })
  const realmNameById = new Map(realms.map((r) => [r.id, r.name]))
  const guildFamiliars = familiarQueries.flatMap((q) => q.data ?? [])

  const [addingMember, setAddingMember] = useState(false)
  const [memberKeeperId, setMemberKeeperId] = useState('')
  const [memberRole, setMemberRole] = useState('member')
  const [savingMember, setSavingMember] = useState(false)

  const [creatingRealm, setCreatingRealm] = useState(false)
  const [realmName, setRealmName] = useState('')
  const [realmSlug, setRealmSlug] = useState('')
  const [realmDesc, setRealmDesc] = useState('')
  const [savingRealm, setSavingRealm] = useState(false)

  function onRealmName(v: string) {
    setRealmName(v)
    setRealmSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
  }

  async function addMember() {
    setSavingMember(true)
    try {
      await api.addMember(id!, memberKeeperId, memberRole)
      setAddingMember(false)
      setMemberKeeperId('')
      qc.invalidateQueries({ queryKey: ['members', id] })
    } finally { setSavingMember(false) }
  }

  async function removeMember(keeperId: string) {
    await api.removeMember(id!, keeperId)
    qc.invalidateQueries({ queryKey: ['members', id] })
  }

  async function createRealm() {
    setSavingRealm(true)
    try {
      await api.createRealm(id!, realmName, realmSlug, realmDesc || undefined)
      setCreatingRealm(false)
      setRealmName('')
      setRealmSlug('')
      setRealmDesc('')
      qc.invalidateQueries({ queryKey: ['realms', id] })
    } finally { setSavingRealm(false) }
  }

  return (
    <div>
      <div className="cp-page-header">
        <div>
          <div className="cp-page-title">{circle?.name ?? '…'}</div>
          <div className="cp-page-subtitle">{circle?.slug}</div>
        </div>
      </div>

      {/* Realms */}
      <div style={{ marginBottom: 32 }}>
        <div className="cp-page-header" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Realms</div>
          {!creatingRealm && (
            <Button variant="ghost" size="sm" onClick={() => setCreatingRealm(true)}>+ Realm</Button>
          )}
        </div>

        {creatingRealm && (
          <Card style={{ marginBottom: 12 }}>
            <div className="cp-form">
              <div className="cp-field">
                <label className="cp-label">Name</label>
                <Input placeholder="e.g. Client Portal" value={realmName} onChange={(e) => onRealmName(e.target.value)} autoFocus />
              </div>
              <div className="cp-field">
                <label className="cp-label">Slug</label>
                <Input value={realmSlug} onChange={(e) => setRealmSlug(e.target.value)} />
              </div>
              <div className="cp-field">
                <label className="cp-label">Description <span style={{ fontWeight: 400, color: 'var(--ae-color-text-muted)' }}>(optional)</span></label>
                <Input value={realmDesc} onChange={(e) => setRealmDesc(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setCreatingRealm(false)}>Cancel</Button>
                <Button size="sm" onClick={createRealm} disabled={savingRealm || !realmName || !realmSlug}>
                  {savingRealm ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {realms.length === 0 && !creatingRealm && <p className="cp-empty">No Realms yet.</p>}
        <div className="cp-card-grid">
          {realms.map((r) => (
            <Link key={r.id} to={`/realms/${r.id}`} className="cp-familiar-card">
              <div className="cp-familiar-name">{r.name}</div>
              {r.description && <div className="cp-page-subtitle">{r.description}</div>}
            </Link>
          ))}
        </div>
      </div>

      <hr className="cp-divider" />

      {/* Guild familiars — the familiars serving this Circle's Realms. */}
      <div style={{ marginBottom: 32 }}>
        <div className="cp-page-header" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Guild familiars</div>
        </div>
        {guildFamiliars.length === 0 && (
          <p className="cp-empty">
            No Guild familiars yet. Open a Realm to create one for it.
          </p>
        )}
        <div className="cp-card-grid">
          {guildFamiliars.map((f) => (
            <Link key={f.id} to={`/familiar/${f.id}/chat`} className="cp-familiar-card">
              <div className="cp-familiar-name">{f.name}</div>
              <div className="cp-familiar-meta">
                <Badge variant="neutral">
                  {(f.realm_id && realmNameById.get(f.realm_id)) || 'Realm'}
                </Badge>
                <Badge variant={f.status === 'active' ? 'success' : f.status === 'suspended' ? 'error' : 'neutral'}>
                  {f.status}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <hr className="cp-divider" />

      {/* Members */}
      <div>
        <div className="cp-page-header" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Members</div>
          {!addingMember && (
            <Button variant="ghost" size="sm" onClick={() => setAddingMember(true)}>+ Member</Button>
          )}
        </div>

        {addingMember && (
          <Card style={{ marginBottom: 12 }}>
            <div className="cp-form">
              <div className="cp-field">
                <label className="cp-label">Keeper ID</label>
                <Input placeholder="keeper-id" value={memberKeeperId} onChange={(e) => setMemberKeeperId(e.target.value)} autoFocus />
              </div>
              <div className="cp-field">
                <label className="cp-label">Role</label>
                <select className="cp-select" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                  <option value="member">Member</option>
                  <option value="elder">Elder</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setAddingMember(false)}>Cancel</Button>
                <Button size="sm" onClick={addMember} disabled={savingMember || !memberKeeperId}>
                  {savingMember ? 'Adding…' : 'Add'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Card>
          <Table>
            <Thead>
              <Tr><Th>Keeper</Th><Th>Role</Th><Th>Joined</Th><Th /></Tr>
            </Thead>
            <Tbody>
              {members.map((m) => (
                <Tr key={m.keeper_id}>
                  <Td>{m.keeper_id}</Td>
                  <Td><Badge variant="neutral" style={{ textTransform: 'capitalize' }}>{m.role}</Badge></Td>
                  <Td style={{ color: 'var(--ae-color-text-muted)' }}>{new Date(m.joined_at).toLocaleDateString()}</Td>
                  <Td>
                    {m.role !== 'warden' && (
                      <Button variant="ghost" size="sm" onClick={() => removeMember(m.keeper_id)}>Remove</Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Card>
      </div>
    </div>
  )
}
